import React from 'react';
import { fmtDateTime } from '../../lib/format';
import { Q_STATUS_COLORS, inr2, fmtDMY } from '../../lib/quotations';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { InfoRow } from './SummaryShared';
import { Pencil, Download, Mail, MessageCircle } from 'lucide-react';

const EmailHistory = ({ emails }) => (
  <div className="space-y-2" data-testid="summary-quotation-emails">
    <div className="text-xs font-semibold flex items-center gap-1.5"><Mail size={12} /> Recent Emails</div>
    {emails.length === 0 && <div className="text-xs text-muted-foreground">Not emailed yet</div>}
    {emails.map((e) => (
      <div key={e.id} className="rounded-lg border bg-muted/30 p-2 text-[11px]">
        <div className="flex justify-between"><span className="font-medium truncate">{(e.to || []).join(', ')}</span>
          <span className={e.ok ? 'text-emerald-600' : 'text-red-500'}>{e.ok ? 'Delivered' : 'Failed'}</span></div>
        <div className="text-muted-foreground">{fmtDateTime(e.created_at)} · by {e.sent_by_name}</div>
      </div>
    ))}
  </div>
);

export const QuotationSummary = ({ data, onOpenFull, onDownload, onSend, onWhatsApp }) => {
  const q = data.quotation;
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading font-bold text-lg leading-tight" data-testid="summary-quotation-number">{q.number}</div>
          <div className="text-xs text-muted-foreground capitalize">{q.doc_type}{q.subject ? ` \u00b7 ${q.subject}` : ''}</div>
        </div>
        <Badge variant="outline" className={`shrink-0 ${Q_STATUS_COLORS[q.status] || ''}`} data-testid="summary-quotation-status">{q.status}</Badge>
      </div>
      <div className="rounded-xl border p-3 text-center">
        <div className="font-heading text-xl font-bold text-primary" data-testid="summary-quotation-total">{inr2(q.totals?.grand_total)}</div>
        <div className="text-[10px] text-muted-foreground uppercase">Grand Total ({q.item_count} items)</div>
      </div>
      <div>
        <InfoRow label="Client" value={q.client?.name} testId="summary-quotation-client" />
        <InfoRow label="Contact" value={q.client?.contact_person} />
        <InfoRow label="Place of Supply" value={q.client?.place_of_supply} />
        <InfoRow label="Document Date" value={fmtDMY(q.date)} />
        <InfoRow label="Valid Until" value={fmtDMY(q.valid_until)} />
        <InfoRow label="Created By" value={q.created_by_name} />
        <InfoRow label="Client Viewed" value={q.viewed_at ? fmtDateTime(q.viewed_at) : 'Not yet'} />
      </div>
      <Separator />
      <EmailHistory emails={data.recent_emails || []} />
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" onClick={onDownload} data-testid="summary-download-pdf-button">
          <Download size={14} className="mr-1.5" /> PDF
        </Button>
        <Button variant="outline" onClick={onSend} data-testid="summary-send-button">
          <Mail size={14} className="mr-1.5" /> Send
        </Button>
        <Button variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          onClick={onWhatsApp} data-testid="summary-whatsapp-button">
          <MessageCircle size={14} className="mr-1.5" /> Chat
        </Button>
      </div>
      <Button className="w-full" onClick={onOpenFull} data-testid="summary-open-quotation-button">
        <Pencil size={14} className="mr-1.5" /> View Full / Edit
      </Button>
    </div>
  );
};
